import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useValidateZipCodes } from 'entities/campaign';
import { Button } from 'shared/ui/Button';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { showDefaultErrorToast } from 'shared/lib/notifications';

import { ZipInput } from './ZipInput';
import { useCampaignId, useCampaignZipCodes, useCreateCampaignStoreActions } from './CreateCampaignStoreHook';

type StepTargetAreaProps = {
  onBack: () => void;
  onNext: () => void;
};

type FormType = {
  zipCodes: string;
};

const ZIP_INPUT_COLLAPSE_HEIGHT = 112;
const INPUT_NAME = 'zipCodes';

export function StepTargetArea(props: StepTargetAreaProps) {
  const { onBack, onNext } = props;
  const { updateStepData } = useCreateCampaignStoreActions();
  const zipCodes = useCampaignZipCodes();
  const campaignId = useCampaignId();
  const { mutateAsync, isLoading, reset } = useValidateZipCodes(campaignId);
  const methods = useForm<FormType>({
    defaultValues: {
      zipCodes,
    },
  });
  const { handleSubmit, control } = methods;
  const textAreaValue = useWatch({ control, name: INPUT_NAME });
  const textAreaHasValue = !!textAreaValue;

  const backClickHandler = () => {
    updateStepData({ zipCodes: textAreaValue });
    onBack();
  };

  const onSubmit = async ({ zipCodes }: FormType) => {
    reset();

    const { data } = await mutateAsync({ payload: { zipCodes } });

    try {
      updateStepData({ zipCodes, ...data.zipCodes });
      onNext();
    } catch (e) {
      showDefaultErrorToast();
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="w-138">
          <div className="text-center text-xl font-medium tracking-normal">Define target area</div>
          <div className="mt-2 text-center text-sm font-normal">
            Each ZIP Code can be placed on a new line or separated by a comma.
          </div>
          <ZipInput
            collapseHeight={ZIP_INPUT_COLLAPSE_HEIGHT}
            className="mt-8"
            textAreaName={INPUT_NAME}
            isLightPlaceholder
            {...getDataAutoTestAttributes(['textarea-create-wizard-zip'])}
          />
          <div className="mt-8 flex justify-between">
            <Button
              type="button"
              onClick={backClickHandler}
              variant="outline-second"
              {...getDataAutoTestAttributes(['button-create-wizard-back'])}
            >
              Back
            </Button>
            <Button
              type="submit"
              disabled={!textAreaHasValue}
              loading={isLoading}
              {...getDataAutoTestAttributes(['button-create-wizard-next'])}
            >
              Next
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
